import classNames from 'classnames'
import React from 'react'
import { useTranslation } from 'react-i18next'

import {
  StyledButton,
  type StyledButtonProps,
} from '~/components/elements/StyledButton'
import {
  SuspenseDialog,
  type SuspenseDialogProps,
} from '~/components/elements/SuspenseDialog'

import styles from './ExecuteCancelDialog.css'

type ExecuteCancelDialogMode =
  'create' | 'delete' | 'duplicate' | 'settings' | 'update'

const executeButtonStyleMap: Record<
  ExecuteCancelDialogMode,
  StyledButtonProps['buttonStyle']
> = {
  create: 'success',
  delete: 'alert',
  duplicate: 'success',
  settings: 'normal',
  update: 'success',
}

export type ExecuteCancelDialogProps = React.PropsWithChildren<
  Pick<SuspenseDialogProps, 'executing' | 'loading' | 'title'> & {
    className?: string
    'data-testid'?: string
    disabledExecute?: boolean
    executeButtonText?: string
    mode: ExecuteCancelDialogMode
    onCancel?: () => void
    onExecute?: () => void
    width?: '100%' | 'auto'
  }
>

export const ExecuteCancelDialog = ({
  children,
  className,
  'data-testid': dataTestId = 'components-elements-execute-cancel-dialog',
  disabledExecute = false,
  executeButtonText: executeButtonTextOverride,
  executing,
  loading,
  mode,
  onCancel,
  onExecute,
  title,
  width = 'auto',
}: ExecuteCancelDialogProps) => {
  const { t } = useTranslation()
  const executeButtonTextMap = React.useMemo(
    () => ({
      create: t('add_7'),
      delete: t('delete_6'),
      settings: t('save_1'),
      update: t('update'),
      duplicate: t('duplicate'),
    }),
    [t],
  )
  const executeButtonText = React.useMemo(
    () => executeButtonTextOverride ?? executeButtonTextMap[mode],
    [executeButtonTextOverride, executeButtonTextMap, mode],
  )

  const handleExecuteButtonClick = React.useCallback<
    EH<StyledButtonProps, 'onClick'>
  >(() => {
    onExecute?.()
  }, [onExecute])

  return (
    <SuspenseDialog
      actions={
        <>
          <StyledButton buttonStyle="light" onClick={onCancel}>
            {t('cancel')}
          </StyledButton>

          <StyledButton
            buttonStyle={executeButtonStyleMap[mode]}
            disabled={disabledExecute}
            onClick={handleExecuteButtonClick}
          >
            {executeButtonText}
          </StyledButton>
        </>
      }
      className={classNames(
        styles.root,
        styles[`width-${width}`],
        styles[`mode-${mode}`],
        className,
      )}
      data-testid={dataTestId}
      executing={executing}
      loading={loading}
      title={title}
      onClose={onCancel}
    >
      {children}
    </SuspenseDialog>
  )
}
